import React, { useEffect, useRef, useState } from 'react'
import { useSnapshot } from 'valtio'
import { QRCodeSVG } from 'qrcode.react'
import { openURL } from 'renderer/viewer/lib/simpleUtils'
import { checkUtopiaUsernameExists, exchangeMicrosoftProof } from '../../api/utopiaAuthApi'
import {
  utopiaAuthUiState,
  type UtopiaMicrosoftVerifiedProfile,
} from './utopiaAuthState'
import {
  verifyMicrosoftOwnsMinecraft,
  type MicrosoftDeviceCode,
} from './utopiaMicrosoftAuth'
import styles from './utopiaLanding.module.css'

type Props = {
  onVerified: (profile: UtopiaMicrosoftVerifiedProfile) => void
}

export default function UtopiaMicrosoftAuthBlock ({ onVerified }: Props) {
  const { microsoftVerifiedProfile } = useSnapshot(utopiaAuthUiState)
  const [loading, setLoading] = useState(false)
  const [checkingAccount, setCheckingAccount] = useState(false)
  const [deviceCode, setDeviceCode] = useState<MicrosoftDeviceCode | null>(null)
  const [deviceCodeExpiresAt, setDeviceCodeExpiresAt] = useState(0)
  const [deviceCodeTimeLeft, setDeviceCodeTimeLeft] = useState('')
  const [localError, setLocalError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => () => {
    abortRef.current?.abort()
  }, [])

  useEffect(() => {
    if (!deviceCodeExpiresAt) {
      setDeviceCodeTimeLeft('')
      return
    }
    const tick = () => {
      const sec = Math.max(0, Math.ceil((deviceCodeExpiresAt - Date.now()) / 1000))
      if (sec <= 0) {
        setDeviceCodeTimeLeft('Code expiré')
        return
      }
      const m = Math.floor(sec / 60)
      const s = sec % 60
      setDeviceCodeTimeLeft(`${m}:${s.toString().padStart(2, '0')}`)
    }
    tick()
    const id = window.setInterval(tick, 1000)
    return () => window.clearInterval(id)
  }, [deviceCodeExpiresAt])

  const cancel = () => {
    abortRef.current?.abort()
    abortRef.current = null
    setLoading(false)
    setCheckingAccount(false)
    setDeviceCode(null)
    setDeviceCodeExpiresAt(0)
  }

  const startMicrosoftAuth = async () => {
    setLocalError(null)
    setDeviceCode(null)
    setDeviceCodeExpiresAt(0)
    utopiaAuthUiState.microsoftVerifiedProfile = null
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    setLoading(true)

    const result = await verifyMicrosoftOwnsMinecraft({
      signal: controller.signal,
      onDeviceCode: data => {
        setDeviceCode(data)
        setDeviceCodeExpiresAt(Date.now() + (data.expires_in || 900) * 1000)
      },
    })

    if (controller.signal.aborted) return

    setLoading(false)
    setDeviceCode(null)

    if (!result.ok) {
      abortRef.current = null
      if (result.reason !== 'cancelled') setLocalError(result.message)
      return
    }

    setCheckingAccount(true)
    let utopiaAccountExists = false
    let utopiaUsername = result.profileName
    let proofToken = ''
    let proofExpiresAt = 0
    try {
      const [check, proof] = await Promise.all([
        checkUtopiaUsernameExists(result.profileName),
        exchangeMicrosoftProof(result.minecraftAccessToken),
      ])
      utopiaAccountExists = check.exists
      if (check.username) utopiaUsername = check.username
      proofToken = proof.proofToken
      proofExpiresAt = proof.expiresAt
    } catch (err) {
      console.warn('[utopia] vérification compte / preuve Microsoft:', err)
      setLocalError(err instanceof Error ? err.message : String(err))
      setCheckingAccount(false)
      abortRef.current = null
      return
    } finally {
      setCheckingAccount(false)
      abortRef.current = null
    }

    const profile: UtopiaMicrosoftVerifiedProfile = {
      name: result.profileName,
      utopiaAccountExists,
      utopiaUsername,
      proofToken,
      proofExpiresAt,
    }
    utopiaAuthUiState.microsoftVerifiedProfile = profile
    onVerified(profile)
  }

  if (microsoftVerifiedProfile) {
    const { name, utopiaAccountExists, utopiaUsername } = microsoftVerifiedProfile
    return (
      <div className={styles.authMicrosoftVerified}>
        <span className={styles.authMicrosoftVerifiedIcon} aria-hidden>✓</span>
        <p className={styles.authMicrosoftVerifiedText}>
          Compte Minecraft détecté : <strong>{name}</strong>
        </p>
        {utopiaAccountExists ? (
          <p className={styles.authMicrosoftVerifiedHint}>
            Un compte Utopie existe déjà pour <strong>{utopiaUsername}</strong>.
            {' '}Utilisez l&apos;onglet <strong>Se connecter</strong> avec votre mot de passe Utopie.
          </p>
        ) : (
          <p className={styles.authMicrosoftVerifiedHint}>
            Aucun compte Utopie pour ce pseudo. Créez votre compte dans l&apos;onglet{' '}
            <strong>S&apos;inscrire</strong> (pseudo identique à Minecraft).
          </p>
        )}
        <button
          type="button"
          className={styles.authMicrosoftChange}
          onClick={() => {
            utopiaAuthUiState.microsoftVerifiedProfile = null
            setLocalError(null)
          }}
        >
          Changer de compte Microsoft
        </button>
      </div>
    )
  }

  return (
    <div className={styles.authMicrosoftBlock}>
      <button
        type="button"
        className={styles.authMicrosoftBtn}
        disabled={loading || checkingAccount}
        onClick={() => void startMicrosoftAuth()}
      >
        {loading
          ? 'Vérification Microsoft…'
          : checkingAccount
            ? 'Vérification du compte Utopie…'
            : 'Connectez-vous avec votre compte Microsoft'}
      </button>

      {deviceCode && (() => {
        const directLink = `https://microsoft.com/link?otc=${encodeURIComponent(deviceCode.user_code)}`
        const loginHost = deviceCode.verification_uri.replace(/^https?:\/\//, '').replace(/\/$/, '')
        return (
          <div className={styles.authMicrosoftDevice}>
            <p className={styles.authMicrosoftDeviceTitle}>Connexion Microsoft</p>
            <div className={styles.authMicrosoftDeviceRow}>
              <div className={styles.authMicrosoftDeviceMain}>
                <p className={styles.authMicrosoftDeviceCode}>{deviceCode.user_code}</p>
                {deviceCodeTimeLeft && (
                  <p className={styles.authMicrosoftDeviceTimer}>
                    En attente… {deviceCodeTimeLeft}
                  </p>
                )}
              </div>
              <div className={styles.authMicrosoftQr} aria-hidden>
                <QRCodeSVG
                  value={directLink}
                  size={72}
                  level="M"
                  bgColor="#ffffff"
                  fgColor="#1a1a1a"
                />
              </div>
            </div>
            <p className={styles.authMicrosoftDeviceHint}>
              Scannez le QR code ou ouvrez le lien, puis entrez le code affiché ci-dessus.
            </p>
            <button
              type="button"
              className={styles.authMicrosoftDirectLink}
              onClick={() => openURL(directLink, true)}
            >
              Ouvrir le lien Microsoft (code prérempli)
            </button>
            <button
              type="button"
              className={styles.authMicrosoftAltLink}
              onClick={() => openURL(deviceCode.verification_uri, true)}
            >
              {loginHost}
            </button>
            <button type="button" className={styles.authMicrosoftCancel} onClick={cancel}>
              Annuler
            </button>
          </div>
        )
      })()}

      {localError && (
        <p className={styles.authMicrosoftError} role="alert">{localError}</p>
      )}
    </div>
  )
}
